// src/components/accueil/personnaliser/FilesEditables.tsx — SCE Issues
// Les compteurs en mode « Personnaliser » (lot 4 du chantier du 03/11,
// docs/chantier-accueil.md) : on les glisse pour changer leur ordre, on en
// retire (jamais une file imposée : « À qualifier » chez les coordinateurs), on
// remet une file retirée, et on règle chacune : le délai de « Sans activité »,
// un seuil d'alerte (point rouge sur le compteur), l'affichage en carte.
// Lot 5 : « + File » compose une file en bulles ou reprend une vue personnelle
// de « Tous les problèmes » (la file reste liée à la vue) ; six files au plus.

import { useRef, useState, type DragEvent, type JSX } from 'react'
import { ArrowLeft, ArrowRight, Bell, EyeOff, GripVertical, LayoutPanelLeft, Link2, Lock, LockOpen, MoreHorizontal, Pencil, Plus, SlidersHorizontal, Trash2 } from 'lucide-react'
import { Popover } from '@/components/ui/Popover'
import {
  ajouterFile,
  carteDeFile,
  DELAIS_SANS_ACTIVITE,
  deplacerFile,
  libelleAlerte,
  masquerFile,
  reglerFile,
  retirable,
  type ModeleAccueil,
  type ReglageFile,
} from '@/utils/dispositionAccueil'
import { MAX_FILES, type FileComposee } from '@/utils/filesComposees'
import type { VuePerso } from '@/utils/tableauProblemes'

export interface FilesEditablesProps {
  files: string[]
  /** Toutes les files connues (pour remettre une file retirée), dans l'ordre du modèle. */
  toutes: readonly { id: string; libelle: string; nombre: number }[]
  reglages: Record<string, ReglageFile>
  modele: ModeleAccueil
  /** Les cartes déjà posées dans les colonnes (« Afficher aussi en carte »). */
  cartes: readonly string[]
  onFiles: (files: string[]) => void
  onReglages: (r: Record<string, ReglageFile>) => void
  onAfficherEnCarte: (idFile: string) => void
  /** La file composée de cet identifiant, s'il en est une (lot 5). */
  composeeDe: (id: string) => FileComposee | undefined
  /** Les vues personnelles de /issues, à reprendre en file. */
  vues: readonly VuePerso[]
  /** Ouvre le composeur : une file à modifier, ou null pour une nouvelle. */
  onComposer: (f: FileComposee | null) => void
  onEpingler: (vue: VuePerso) => void
  /**
   * Modèle de /admin (lot 6) : ni réglages personnels (délai, alerte), ni file
   * composée ; le cadenas « Imposer à tous » à la place.
   */
  onImposer?: (id: string) => void
}

export function FilesEditables({ files, toutes, reglages, modele, cartes, onFiles, onReglages, onAfficherEnCarte, composeeDe, vues, onComposer, onEpingler, onImposer }: FilesEditablesProps): JSX.Element {
  const pourModele = !!onImposer
  const [glisse, setGlisse] = useState<string | null>(null)
  const [cible, setCible] = useState<number | null>(null)
  const rangee = useRef<HTMLDivElement>(null)
  const [ajout, setAjout] = useState<HTMLButtonElement | null>(null)
  const libelle = (id: string): string => toutes.find((f) => f.id === id)?.libelle ?? id
  const retirees = toutes.filter((f) => !files.includes(f.id))
  const plein = files.length >= MAX_FILES
  const vuesEpinglees = new Set(files.flatMap((id) => composeeDe(id)?.vue ?? []))

  function rangSous(x: number): number {
    const tuiles = [...(rangee.current?.querySelectorAll<HTMLElement>('[data-file]') ?? [])].filter((t) => t.dataset.file !== glisse)
    const i = tuiles.findIndex((t) => {
      const r = t.getBoundingClientRect()
      return x < r.left + r.width / 2
    })
    return i < 0 ? tuiles.length : i
  }

  function surSurvol(e: DragEvent<HTMLDivElement>): void {
    if (!glisse) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const r = rangSous(e.clientX)
    if (r !== cible) setCible(r)
  }

  function surDepot(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault()
    const id = glisse
    const r = rangSous(e.clientX)
    setGlisse(null)
    setCible(null)
    if (id) onFiles(deplacerFile(files, id, r))
  }

  const visibles = files.filter((id) => id !== glisse)

  return (
    <div className="flex items-stretch" style={{ gap: 12 }}>
      <div
        ref={rangee}
        role="list"
        aria-label="Files, dans l’ordre des compteurs"
        className="perso-files"
        style={{ gridTemplateColumns: `repeat(${files.length}, minmax(0, 1fr))` }}
        onDragOver={surSurvol}
        onDrop={surDepot}
      >
        {files.map((id, i) => {
          const f = toutes.find((x) => x.id === id)
          const imposee = !retirable(id, modele)
          const r = reglages[id]
          const rang = visibles.indexOf(id)
          return (
            <TuileEditable
              key={id}
              id={id}
              libelle={libelle(id)}
              nombre={f?.nombre ?? 0}
              trait={cible !== null && rang === cible}
              traitApres={cible !== null && cible === visibles.length && rang === visibles.length - 1}
              glisse={glisse === id}
              imposee={imposee}
              derniere={files.length <= 1}
              reglage={r}
              composee={composeeDe(id)}
              nomVue={vues.find((v) => v.id === composeeDe(id)?.vue)?.nom}
              onModifier={() => onComposer(composeeDe(id) ?? null)}
              enCarte={cartes.includes(carteDeFile(id))}
              peutGauche={i > 0}
              peutDroite={i < files.length - 1}
              onDebut={(e) => {
                e.dataTransfer.effectAllowed = 'move'
                e.dataTransfer.setData('text/plain', id)
                setGlisse(id)
              }}
              onFin={() => {
                setGlisse(null)
                setCible(null)
              }}
              onDecaler={(sens) => onFiles(deplacerFile(files, id, i + sens))}
              onRetirer={() => onFiles(masquerFile(files, id, modele))}
              onRegler={(changement) => onReglages(reglerFile(reglages, id, changement))}
              onAfficherEnCarte={() => onAfficherEnCarte(id)}
              onImposer={onImposer && (() => onImposer(id))}
              imposeeParModele={modele.imposees.includes(id)}
            />
          )
        })}
      </div>
      <button type="button" className="perso-ajout perso-ajout-file" aria-expanded={!!ajout} onClick={(e) => setAjout(ajout ? null : e.currentTarget)}>
        <Plus size={16} aria-hidden="true" />
        File
      </button>
      {ajout && (
        <Popover ancre={ajout} onClose={() => setAjout(null)} titre="Ajouter une file" largeur={300} alignerADroite>
          {plein && <div className="popover-vide">{MAX_FILES} files au plus : retirez-en une avant d’en ajouter.</div>}
          <ul role="menu" className="perso-liste-menu">
            {!pourModele && (
              <li role="none">
                <button type="button" role="menuitem" disabled={plein} onClick={() => { setAjout(null); onComposer(null) }}>
                  <SlidersHorizontal size={16} />
                  Composer une file…
                </button>
              </li>
            )}
            {!pourModele && vues.length > 0 && <li role="none" className="perso-separateur perso-sous-titre">Depuis une vue de Tous les problèmes</li>}
            {vues.map((v) => (
              <li key={v.id} role="none">
                <button type="button" role="menuitem" disabled={plein || vuesEpinglees.has(v.id)} onClick={() => { setAjout(null); onEpingler(v) }}>
                  <Link2 size={16} />
                  {v.nom}
                  {vuesEpinglees.has(v.id) && <span style={{ marginLeft: 'auto', fontSize: 12 }}>déjà là</span>}
                </button>
              </li>
            ))}
            {retirees.length > 0 && <li role="none" className="perso-separateur perso-sous-titre">Remettre</li>}
            {retirees.map((f) => (
              <li key={f.id} role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={plein}
                  onClick={() => {
                    setAjout(null)
                    onFiles(ajouterFile(files, f.id))
                  }}
                >
                  {f.libelle}
                  <span style={{ marginLeft: 'auto', color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{f.nombre}</span>
                </button>
              </li>
            ))}
          </ul>
        </Popover>
      )}
    </div>
  )
}

function TuileEditable({
  id,
  libelle,
  nombre,
  trait,
  traitApres,
  glisse,
  imposee,
  derniere,
  reglage,
  composee,
  nomVue,
  onModifier,
  enCarte,
  peutGauche,
  peutDroite,
  onDebut,
  onFin,
  onDecaler,
  onRetirer,
  onRegler,
  onAfficherEnCarte,
  onImposer,
  imposeeParModele,
}: {
  id: string
  libelle: string
  nombre: number
  trait: boolean
  traitApres: boolean
  glisse: boolean
  imposee: boolean
  derniere: boolean
  reglage: ReglageFile | undefined
  composee: FileComposee | undefined
  /** Nom de la vue de /issues à laquelle la file est liée. */
  nomVue: string | undefined
  onModifier: () => void
  enCarte: boolean
  peutGauche: boolean
  peutDroite: boolean
  onDebut: (e: DragEvent<HTMLDivElement>) => void
  onFin: () => void
  onDecaler: (sens: -1 | 1) => void
  onRetirer: () => void
  onRegler: (changement: Partial<ReglageFile>) => void
  onAfficherEnCarte: () => void
  onImposer?: () => void
  imposeeParModele: boolean
}): JSX.Element {
  const [menu, setMenu] = useState<HTMLButtonElement | null>(null)
  const [reglages, setReglages] = useState<HTMLButtonElement | null>(null)
  const seuil = libelleAlerte(reglage)
  return (
    <div
      role="listitem"
      data-file={id}
      className="perso-tuile"
      data-glisse={glisse || undefined}
      data-trait={trait ? 'avant' : traitApres ? 'apres' : undefined}
      draggable
      onDragStart={onDebut}
      onDragEnd={onFin}
      title="Glisser pour changer l’ordre"
    >
      <GripVertical size={16} aria-hidden="true" style={{ flexShrink: 0, color: 'var(--color-gray-60)' }} />
      <span style={{ fontSize: 24, fontWeight: 500, fontVariantNumeric: 'tabular-nums', minWidth: 28 }}>{nombre}</span>
      <span className="flex flex-col" style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{libelle}</span>
        <span className="flex items-center" style={{ gap: 4, fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {imposee && <Lock size={12} aria-hidden="true" />}
          {imposee ? 'Imposée' : seuil ? <><Bell size={12} aria-hidden="true" />{seuil}</> : composee?.vue ? <><Link2 size={12} aria-hidden="true" />Vue liée</> : composee ? 'File composée' : 'Pas d’alerte'}
        </span>
      </span>
      <button type="button" className="perso-menu" aria-label={`Régler « ${libelle} »`} aria-expanded={!!menu} onClick={(e) => setMenu(menu ? null : e.currentTarget)}>
        <MoreHorizontal size={18} aria-hidden="true" />
      </button>
      {menu && (
        <Popover ancre={menu} onClose={() => setMenu(null)} titre={`Régler « ${libelle} »`} largeur={270} alignerADroite>
          <ul role="menu" className="perso-liste-menu">
            {composee && !composee.vue && (
              <li role="none">
                <button type="button" role="menuitem" onClick={() => { setMenu(null); onModifier() }}>
                  <Pencil size={16} />
                  Modifier la file…
                </button>
              </li>
            )}
            {composee?.vue && (
              <li role="none" className="perso-sous-titre">
                Liée à la vue « {nomVue ?? '?'} » : elle suit ses filtres
              </li>
            )}
            {onImposer ? (
              <li role="none">
                <button type="button" role="menuitem" onClick={() => { setMenu(null); onImposer() }}>
                  {imposeeParModele ? <LockOpen size={16} /> : <Lock size={16} />}
                  {imposeeParModele ? 'Ne plus imposer' : 'Imposer à tous (cadenas)'}
                </button>
              </li>
            ) : (
              <li role="none">
                <button type="button" role="menuitem" onClick={() => { setReglages(menu); setMenu(null) }}>
                  <SlidersHorizontal size={16} />
                  {id === 'sansActivite' ? 'Délai et alerte…' : 'Alerte…'}
                </button>
              </li>
            )}
            <li role="none">
              <button type="button" role="menuitem" disabled={enCarte} onClick={() => { setMenu(null); onAfficherEnCarte() }}>
                <LayoutPanelLeft size={16} />
                {enCarte ? 'Déjà affichée en carte' : 'Afficher aussi en carte'}
              </button>
            </li>
            <li role="none">
              <button type="button" role="menuitem" disabled={!peutGauche} onClick={() => { setMenu(null); onDecaler(-1) }}>
                <ArrowLeft size={16} />
                Vers la gauche
              </button>
            </li>
            <li role="none">
              <button type="button" role="menuitem" disabled={!peutDroite} onClick={() => { setMenu(null); onDecaler(1) }}>
                <ArrowRight size={16} />
                Vers la droite
              </button>
            </li>
            <li role="none" className="perso-separateur">
              <button type="button" role="menuitem" disabled={imposee || derniere} onClick={() => { setMenu(null); onRetirer() }}>
                {imposee ? <Lock size={16} /> : composee ? <Trash2 size={16} /> : <EyeOff size={16} />}
                {imposee ? 'Imposée : ne se retire pas' : derniere ? 'Il faut garder une file' : composee ? (enCarte ? 'Retirer des compteurs' : 'Supprimer la file') : 'Retirer des compteurs'}
              </button>
            </li>
          </ul>
        </Popover>
      )}
      {reglages && <ReglagesFile ancre={reglages} libelle={libelle} delai={id === 'sansActivite'} reglage={reglage} onRegler={onRegler} onClose={() => setReglages(null)} />}
    </div>
  )
}

function ReglagesFile({
  ancre,
  libelle,
  delai,
  reglage,
  onRegler,
  onClose,
}: {
  ancre: HTMLElement
  libelle: string
  /** La file « Sans activité » : son délai se règle aussi. */
  delai: boolean
  reglage: ReglageFile | undefined
  onRegler: (changement: Partial<ReglageFile>) => void
  onClose: () => void
}): JSX.Element {
  const nombre = (v: string): number | undefined => {
    const n = Number.parseInt(v, 10)
    return Number.isFinite(n) && n > 0 ? n : undefined
  }
  return (
    <Popover ancre={ancre} onClose={onClose} titre={`Régler « ${libelle} »`} largeur={320} alignerADroite>
      <div className="popover-entete">
        <span>{libelle}</span>
      </div>
      <div className="perso-reglages">
        {delai && (
          <fieldset>
            <legend>Sans activité depuis</legend>
            <div className="flex" style={{ gap: 6 }}>
              {DELAIS_SANS_ACTIVITE.map((d) => {
                const actif = (reglage?.delai ?? 7) === d
                return (
                  <button key={d} type="button" className="perso-bulle" aria-pressed={actif} onClick={() => onRegler({ delai: d === 7 ? undefined : d })}>
                    {d} jours
                  </button>
                )
              })}
            </div>
          </fieldset>
        )}
        <fieldset>
          <legend>Point rouge sur le compteur quand…</legend>
          <label className="perso-seuil">
            <input type="checkbox" checked={reglage?.alerteNombre !== undefined} onChange={(e) => onRegler({ alerteNombre: e.target.checked ? 5 : undefined })} />
            plus de
            <input
              type="number"
              min={1}
              max={999}
              inputMode="numeric"
              aria-label="Nombre de problèmes"
              value={reglage?.alerteNombre ?? ''}
              placeholder="5"
              onChange={(e) => onRegler({ alerteNombre: nombre(e.target.value) })}
            />
            problèmes
          </label>
          <label className="perso-seuil">
            <input type="checkbox" checked={reglage?.alerteJours !== undefined} onChange={(e) => onRegler({ alerteJours: e.target.checked ? 3 : undefined })} />
            un problème attend depuis plus de
            <input
              type="number"
              min={1}
              max={365}
              inputMode="numeric"
              aria-label="Nombre de jours"
              value={reglage?.alerteJours ?? ''}
              placeholder="3"
              onChange={(e) => onRegler({ alerteJours: nombre(e.target.value) })}
            />
            jours
          </label>
        </fieldset>
      </div>
    </Popover>
  )
}
